import React, {useMemo, useState} from "react";
import {CreditCardIcon, CheckCircleIcon, ExclamationCircleIcon, CalendarDaysIcon, ClockIcon} from "@heroicons/react/24/outline";
import useTranslate from "../../core/languages/hooks/useTranslate";
import PaymentCardSelector from "./PaymentCardSelector";
import {LuxonLogo} from "../icons/LuxonLogo";
import {formatNumber} from "../../common/utils/number";
import moment from "moment";
import useExchangerGet from "../hooks/useExchangerGet";
import useCurrencyGet from "../hooks/useCurrencyGet";

type PaymentMethod = "card" | "luxon";
type PayMode = "now" | "later";

export interface PaymentPanelPaymentInput {
    selectedCardUuid: string | null;
    payMode: PayMode;
    payLaterIso?: string;
}

interface PaymentPanelProps {
    bookingId: number;
    bookingInternalId: string;
    amount: number;
    currencySymbol: string;
    currency: string; // IRI like "/currencies/6"
    authenticated: boolean;
    preferredCardUuid?: string | null;
    tpvPaymentLastDate?: string | null;
    tpvPaymentInitialPayPercentage?: string | null;
    onCardPay: (input: PaymentPanelPaymentInput) => Promise<void>;
    onLuxonPay: () => Promise<void>;
}

const MethodTab: React.FC<{
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}> = ({active, onClick, children}) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
            active
                ? "bg-white dark:bg-neutral-800 shadow-sm text-neutral-900 dark:text-neutral-100 ring-1 ring-neutral-200 dark:ring-neutral-700"
                : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300"
        }`}
    >
        {children}
    </button>
);

const PayModeToggle: React.FC<{
    mode: PayMode;
    onChange: (m: PayMode) => void;
    payLaterDateStr: string | null;
    t: (key: string, opts?: any) => string;
}> = ({mode, onChange, payLaterDateStr, t}) => (
    <div className="flex gap-2">
        {payLaterDateStr && (
            <>
                <button
                    type="button"
                    onClick={() => onChange("now")}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold border transition-all ${
                        mode === "now"
                            ? "bg-primary-6000 text-white border-primary-6000 shadow-sm"
                            : "bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:border-primary-400"
                    }`}
                >
                    <CreditCardIcon className="w-3.5 h-3.5"/>
                    {t("bookOk.payNow")}
                </button>
                <button
                    type="button"
                    onClick={() => onChange("later")}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold border transition-all ${
                        mode === "later"
                            ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                            : "bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:border-amber-400"
                    }`}
                >
                    <ClockIcon className="w-3.5 h-3.5"/>
                    {t("bookOk.schedulePayment")}
                </button>
            </>
        )}
    </div>
);

const PaymentPanel: React.FC<PaymentPanelProps> = ({
    amount, currencySymbol, currency, authenticated, preferredCardUuid, tpvPaymentLastDate,
    tpvPaymentInitialPayPercentage, onCardPay, onLuxonPay
}) => {
    const t = useTranslate();
    const [activeMethod, setActiveMethod] = useState<PaymentMethod>("card");
    const [selectedCardUuid, setSelectedCardUuid] = useState<string | null>(null);
    const [payMode, setPayMode] = useState<PayMode>("now");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const formattedAmount = `${formatNumber(amount)} ${currencySymbol}`;
    
    // Obtener conversión si existe
    const exchangerQuery = useExchangerGet({
        amount: amount.toString(),
        currency: currency
    });
    const convertedAmount = exchangerQuery.data?.convertedAmount;
    const convertedCurrency = exchangerQuery.data?.convertedCurrency;
    
    // Fetch símbolo de moneda convertida si existe
    const {data: convertedCurrencyData} = useCurrencyGet(convertedCurrency);
    const convertedCurrencySymbol = convertedCurrencyData?.symbol;
    
    const formattedConvertedAmount = convertedAmount && convertedCurrencySymbol
        ? `${formatNumber(parseFloat(convertedAmount))} ${convertedCurrencySymbol}`
        : null;

    // Calcular el porcentaje inicial
    const initialPayPercentage = tpvPaymentInitialPayPercentage
        ? parseFloat(tpvPaymentInitialPayPercentage)
        : 0;

    const initialPayAmount = (amount * initialPayPercentage) / 100;
    const remainingAmount = amount - initialPayAmount;

    const formattedInitialAmount = `${formatNumber(initialPayAmount)} ${currencySymbol}`;
    const formattedRemainingAmount = `${formatNumber(remainingAmount)} ${currencySymbol}`;

    const payLaterDateStr = useMemo(() => {
        if (!tpvPaymentLastDate) return null;
        
        const paymentDate = moment(tpvPaymentLastDate);
        const now = moment();
        
        // Si la fecha de pago ya pasó, retornar null
        if (paymentDate.isBefore(now, 'day')) {
            return null;
        }
        
        return paymentDate.format('DD [de] MMMM [de] YYYY');
    }, [tpvPaymentLastDate]);

    const payLaterIso = useMemo(() => {
        if (!tpvPaymentLastDate) return undefined;

        // Si la fecha de pago ya pasó, retornar undefined
        if (moment(tpvPaymentLastDate).isBefore(moment(), 'day')) {
            return undefined;
        }

        return tpvPaymentLastDate;
    }, [tpvPaymentLastDate]);

    const handleCardPay = async () => {
        if (!selectedCardUuid) return;
        setError(null);
        setIsLoading(true);
        try {
            await onCardPay({
                selectedCardUuid,
                payMode,
                payLaterIso,
            });
            setSuccess(true);
        } catch {
            setError(t("bookOk.paymentError"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleLuxonPay = async () => {
        setError(null);
        setIsLoading(true);
        try {
            await onLuxonPay();
            setSuccess(true);
        } catch {
            setError(t("bookOk.paymentError"));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">

            {/* ── Header: importe pendiente (estilo PriceSummary) ── */}
            <div className="px-4 bg-blue-50/80 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/40">
                <div className="flex items-start gap-4 py-4">
                    <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white dark:bg-neutral-800 shadow-sm">
                            <CreditCardIcon className="w-6 h-6 text-blue-600 dark:text-blue-400"/>
                        </div>
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                            {t("bookOk.totalPending")}
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            {t("bookOk.totalPendingSubtitle")}
                        </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1 py-1">
                        <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">{formattedAmount}</span>
                        {formattedConvertedAmount && (
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">{formattedConvertedAmount}</span>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Tabs de método de pago ── */}
            <div className="px-4 pt-4">
                <div className="flex gap-1.5 p-1 rounded-xl bg-neutral-100 dark:bg-neutral-800">
                    <MethodTab
                        active={activeMethod === "card"}
                        onClick={() => { setActiveMethod("card"); setError(null); }}
                    >
                        <CreditCardIcon className="w-4 h-4"/>
                        {t("bookOk.payByCard")}
                    </MethodTab>
                    <MethodTab
                        active={activeMethod === "luxon"}
                        onClick={() => { setActiveMethod("luxon"); setError(null); }}
                    >
                        <LuxonLogo height={14}/>
                    </MethodTab>
                </div>
            </div>

            {/* ── Contenido del tab activo ── */}
            <div className="px-4 pt-4 pb-5 space-y-4">

                {activeMethod === "card" && (
                    <>
                        <PaymentCardSelector
                            value={selectedCardUuid}
                            onChange={setSelectedCardUuid}
                            authenticated={authenticated}
                            preferredCardUuid={preferredCardUuid}
                        />

                        {/* ── Toggle: pagar ahora / pagar más tarde ── */}
                        <PayModeToggle
                            mode={payMode}
                            onChange={(m) => { setPayMode(m); setError(null); }}
                            payLaterDateStr={payLaterDateStr}
                            t={t}
                        />

                        {/* ── Info de fecha de pago diferido CON DESGLOSE ── */}
                        {payMode === "later" && payLaterDateStr && (
                            <>
                                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                    <CalendarDaysIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"/>
                                    <div className="flex-1 space-y-2">
                                        <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                                            {initialPayPercentage > 0
                                                ? t("bookOk.scheduledPaymentInfoPartialHead")
                                                : t("bookOk.scheduledPaymentInfoTotalHead")}
                                        </p>
                                        <p className="text-xs text-amber-700 dark:text-amber-400">
                                            {initialPayPercentage > 0
                                                ? t("bookOk.scheduledPaymentInfoPartial")
                                                : t("bookOk.scheduledPaymentInfoTotal")}
                                        </p>

                                        {/* ── DESGLOSE DE PAGOS ── */}
                                        <div className="pt-3 border-t border-amber-200 dark:border-amber-800/60 space-y-2">
                                            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                                                {t("bookOk.paymentBreakdown")}
                                            </p>
                                            <div className="flex justify-between text-xs text-amber-700 dark:text-amber-400">
                                                <span>
                                                    {initialPayPercentage > 0
                                                        ? t("bookOk.initialPayment")
                                                        : t("bookOk.preauthorization")}
                                                </span>
                                                <span className="font-semibold">
                                                    {initialPayPercentage > 0 ? formattedInitialAmount : `0,01 ${currencySymbol}`}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-xs text-amber-700 dark:text-amber-400">
                                                <span>{t("bookOk.remainingPayment", {date: payLaterDateStr})}</span>
                                                <span className="font-semibold">{formattedRemainingAmount}</span>
                                            </div>
                                            <div className="flex justify-between text-xs font-bold text-amber-800 dark:text-amber-300 pt-2 border-t border-amber-200 dark:border-amber-800/60">
                                                <span>{t("bookOk.totalAmount")}</span>
                                                <span>{formattedAmount}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <button
                            type="button"
                            onClick={handleCardPay}
                            disabled={!selectedCardUuid || isLoading || success}
                            className={`w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-semibold text-sm text-white transition-colors ${
                                !selectedCardUuid || isLoading || success
                                    ? "opacity-50 cursor-not-allowed bg-primary-6000"
                                    : payMode === "later"
                                        ? "bg-amber-500 hover:bg-amber-600"
                                        : "bg-primary-6000 hover:bg-primary-700"
                            }`}
                        >
                            {success ? (
                                <><CheckCircleIcon className="w-5 h-5"/>{t("bookOk.redirecting")}</>
                            ) : isLoading ? (
                                <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"/>{t("bookOk.processing")}</>
                            ) : payMode === "later" ? (
                                <><ClockIcon className="w-4 h-4"/>{t("bookOk.schedulePayment")}</>
                            ) : (
                                <><CreditCardIcon className="w-4 h-4"/>{t("bookOk.payAmount", {amount: formattedAmount})}</>
                            )}
                        </button>
                    </>
                )}

                {activeMethod === "luxon" && (
                    <>
                        <div className="flex items-center gap-4 p-4 rounded-2xl bg-neutral-50 dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700">
                            <div className="flex-shrink-0 p-2 rounded-xl bg-white dark:bg-neutral-700 shadow-sm">
                                <LuxonLogo height={20}/>
                            </div>
                            <p className="text-sm text-neutral-600 dark:text-neutral-300">
                                {t("bookOk.luxonDescription")}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleLuxonPay}
                            disabled={isLoading || success}
                            className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl font-semibold text-sm text-white bg-neutral-900 hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {success ? (
                                <><CheckCircleIcon className="w-5 h-5"/>{t("bookOk.redirecting")}</>
                            ) : isLoading ? (
                                <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"/>{t("bookOk.processing")}</>
                            ) : (
                                <>{t("bookOk.payWithLuxon", {amount: formattedAmount})}</>
                            )}
                        </button>
                    </>
                )}

                {error && (
                    <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-xl">
                        <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0"/>
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentPanel;
